import { useState } from 'react';
import { Sun, Droplets, Wind, Leaf, Scissors, RefreshCw, TrendingUp, AlertTriangle, Shield, Sparkles, Bug, Sprout, FlaskConical, Moon, Palette, Droplet, Flower2, TreePine, Calendar, Users, ChevronDown, Ruler } from 'lucide-react';
import type { PlantSpecies, LightLevel, HumidityLevel, Toxicity, DifficultyLevel, GrowthRate, OutdoorSuitability } from '@/types';
import { everyNDays } from '@/utils/date';

interface CareTipCardProps {
  species: PlantSpecies;
}

const lightLevelText: Record<LightLevel, string> = {
  LOW: 'Тень / Полутень',
  MEDIUM: 'Рассеянный свет',
  HIGH: 'Яркий свет',
  DIRECT: 'Прямой солнечный свет',
};

const humidityLevelText: Record<HumidityLevel, string> = {
  LOW: 'Низкая',
  MEDIUM: 'Средняя',
  HIGH: 'Высокая',
};

const toxicityText: Record<Toxicity, string> = {
  NONE: 'Безопасно',
  MILD: 'Слабая токсичность',
  MODERATE: 'Умеренная токсичность',
  HIGH: 'Высокая токсичность',
};

const toxicityColor: Record<Toxicity, string> = {
  NONE: 'text-plant-green-500',
  MILD: 'text-yellow-500',
  MODERATE: 'text-orange-500',
  HIGH: 'text-red-500',
};

const difficultyText: Record<DifficultyLevel, string> = {
  BEGINNER: 'Для новичков',
  INTERMEDIATE: 'Средняя сложность',
  ADVANCED: 'Для опытных',
};

const difficultyColor: Record<DifficultyLevel, string> = {
  BEGINNER: 'text-plant-green-500',
  INTERMEDIATE: 'text-yellow-500',
  ADVANCED: 'text-red-500',
};

const growthRateText: Record<GrowthRate, string> = {
  SLOW: 'Медленный',
  MEDIUM: 'Средний',
  FAST: 'Быстрый',
};

const outdoorSuitabilityText: Record<OutdoorSuitability, string> = {
  INDOOR_ONLY: 'Только в помещении',
  SUMMER_OUTDOOR: 'Можно выносить на улицу летом',
  YEAR_ROUND_OUTDOOR: 'Круглогодично на улице',
};

interface TipSectionProps {
  icon: React.ElementType;
  title: string;
  content: string | null;
  highlight?: string;
  highlightClassName?: string;
}

function TipSection({ icon: Icon, title, content, highlight, highlightClassName }: TipSectionProps) {
  if (!content && !highlight) return null;

  return (
    <div className="flex gap-3 tg-card">
      <div className="w-10 h-10 rounded-full glass-circle-green flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-plant-green-500" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm">{title}</h4>
        {highlight && (
          <p className={`font-medium text-sm mt-1 ${highlightClassName || 'text-plant-green-500'}`}>{highlight}</p>
        )}
        {content && (
          <p className="text-tg-hint text-sm mt-1">{content}</p>
        )}
      </div>
    </div>
  );
}

export default function CareTipCard({ species }: CareTipCardProps) {
  const [showDetails, setShowDetails] = useState(false);

  // Check if there are any detail sections to show
  const hasDetails = !!(
    species.soilMix || species.commonProblems || species.commonPests ||
    species.propagationMethods || species.propagationTips || species.dormancyInfo ||
    species.commonVarieties || species.bloomingSeason || species.bloomingTips ||
    species.waterQuality || species.commonNameSynonyms ||
    (species.soilPhMin != null && species.soilPhMax != null) ||
    species.repottingFrequencyMonths != null ||
    species.foliageColor || species.flowerColor ||
    species.seasonalCareNotes || species.companionPlants || species.outdoorSuitability
  );

  return (
    <div className="space-y-3">
      {/* Quick info badges */}
      <div className="flex flex-wrap gap-2">
        {species.difficultyLevel && (
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium glass-circle ${difficultyColor[species.difficultyLevel]}`}>
            <Sparkles className="w-3 h-3" />
            {difficultyText[species.difficultyLevel]}
          </span>
        )}
        {species.toxicity && (
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium glass-circle ${toxicityColor[species.toxicity]}`}>
            {species.toxicity === 'NONE' ? <Shield className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            {toxicityText[species.toxicity]}
          </span>
        )}
        {species.growthRate && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium glass-circle text-tg-hint">
            <TrendingUp className="w-3 h-3" />
            Рост: {growthRateText[species.growthRate]}
          </span>
        )}
        {species.airPurifying && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium glass-circle text-blue-500">
            <Wind className="w-3 h-3" />
            Очищает воздух
          </span>
        )}
        {species.maxHeightCm && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium glass-circle text-tg-hint">
            <Ruler className="w-3 h-3" />
            До {species.maxHeightCm} см
          </span>
        )}
      </div>

      {/* Toxicity warning */}
      {species.toxicity && species.toxicity !== 'NONE' && species.toxicityNotes && (
        <div className="flex gap-3 tg-card border-l-2 border-orange-400">
          <div className="w-10 h-10 rounded-full glass-circle flex items-center justify-center flex-shrink-0">
            <span className="text-lg">🐾</span>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm">Безопасность</h4>
            <p className={`font-medium text-sm mt-1 ${toxicityColor[species.toxicity]}`}>
              {toxicityText[species.toxicity]}
            </p>
            <p className="text-tg-hint text-sm mt-1">{species.toxicityNotes}</p>
          </div>
        </div>
      )}

      {/* === Core care === */}
      <TipSection
        icon={Sun}
        title="Освещение"
        highlight={lightLevelText[species.lightLevel]}
        content={species.lightingTips}
      />

      <TipSection
        icon={Droplets}
        title="Полив"
        highlight={everyNDays(species.wateringFrequencyDays)}
        content={species.wateringTips}
      />

      <TipSection
        icon={Wind}
        title="Влажность"
        highlight={humidityLevelText[species.humidityLevel]}
        content={species.humidityTips}
      />

      <div className="flex gap-3 tg-card">
        <div className="w-10 h-10 rounded-full glass-circle flex items-center justify-center flex-shrink-0">
          <span className="text-lg">🌡️</span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm">Температура</h4>
          <p className="text-plant-green-500 font-medium text-sm mt-1">
            {species.temperatureMin}°C — {species.temperatureMax}°C
          </p>
        </div>
      </div>

      <TipSection
        icon={Leaf}
        title="Подкормка"
        highlight={species.fertilizingFrequencyDays ? everyNDays(species.fertilizingFrequencyDays) : undefined}
        content={species.feedingTips}
      />

      <TipSection
        icon={Scissors}
        title="Обрезка"
        content={species.pruningTips}
      />

      <TipSection
        icon={RefreshCw}
        title="Пересадка"
        content={species.repottingTips}
      />

      {/* === Details section (collapsed by default) === */}
      {hasDetails && (
        <>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full py-2.5 rounded-xl glass-circle text-sm font-medium text-tg-hint flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          >
            {showDetails ? 'Свернуть' : 'Подробнее'}
            <ChevronDown className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
          </button>

          {showDetails && (
            <div className="space-y-3">
              {/* Soil mix */}
              {species.soilMix && (
                <TipSection icon={FlaskConical} title="Грунт" content={species.soilMix} />
              )}

              {/* Common problems */}
              {species.commonProblems && (
                <div className="flex gap-3 tg-card">
                  <div className="w-10 h-10 rounded-full glass-circle flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm">Типичные проблемы</h4>
                    <div className="mt-1 space-y-1">
                      {species.commonProblems.split('; ').map((problem, i) => (
                        <p key={i} className="text-tg-hint text-sm flex items-start gap-1.5">
                          <span className="text-yellow-500 mt-0.5">&#8226;</span>
                          <span>{problem}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Common pests */}
              {species.commonPests && (
                <div className="flex gap-3 tg-card">
                  <div className="w-10 h-10 rounded-full glass-circle flex items-center justify-center flex-shrink-0">
                    <Bug className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm">Вредители</h4>
                    <div className="mt-1 space-y-1">
                      {species.commonPests.split('; ').map((pest, i) => (
                        <p key={i} className="text-tg-hint text-sm flex items-start gap-1.5">
                          <span className="text-red-400 mt-0.5">&#8226;</span>
                          <span>{pest}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Propagation */}
              {(species.propagationMethods || species.propagationTips) && (
                <div className="flex gap-3 tg-card">
                  <div className="w-10 h-10 rounded-full glass-circle-green flex items-center justify-center flex-shrink-0">
                    <Sprout className="w-5 h-5 text-plant-green-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm">Размножение</h4>
                    {species.propagationMethods && (
                      <p className="text-plant-green-500 font-medium text-sm mt-1">{species.propagationMethods}</p>
                    )}
                    {species.propagationTips && (
                      <p className="text-tg-hint text-sm mt-1">{species.propagationTips}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Dormancy */}
              {species.dormancyInfo && (
                <TipSection icon={Moon} title="Период покоя" content={species.dormancyInfo} />
              )}

              {/* Blooming */}
              {(species.bloomingSeason || species.bloomingTips) && (
                <div className="flex gap-3 tg-card">
                  <div className="w-10 h-10 rounded-full glass-circle-green flex items-center justify-center flex-shrink-0">
                    <Flower2 className="w-5 h-5 text-pink-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm">Цветение</h4>
                    {species.bloomingSeason && (
                      <p className="text-pink-500 font-medium text-sm mt-1">{species.bloomingSeason}</p>
                    )}
                    {species.bloomingTips && (
                      <p className="text-tg-hint text-sm mt-1">{species.bloomingTips}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Common varieties */}
              {species.commonVarieties && (
                <TipSection icon={Palette} title="Популярные сорта" content={species.commonVarieties} />
              )}

              {/* Water quality */}
              {species.waterQuality && (
                <TipSection icon={Droplet} title="Качество воды" content={species.waterQuality} />
              )}

              {/* Seasonal care notes */}
              {species.seasonalCareNotes && (
                <TipSection icon={Calendar} title="Сезонный уход" content={species.seasonalCareNotes} />
              )}

              {/* Soil pH */}
              {species.soilPhMin != null && species.soilPhMax != null && (
                <TipSection icon={FlaskConical} title="pH почвы" highlight={`${species.soilPhMin} — ${species.soilPhMax}`} content={null} />
              )}

              {/* Foliage & flower colors */}
              {species.foliageColor && (
                <TipSection icon={Leaf} title="Цвет листьев" highlight={species.foliageColor} content={null} />
              )}
              {species.flowerColor && (
                <TipSection icon={Flower2} title="Цвет цветов" highlight={species.flowerColor} highlightClassName="text-pink-500" content={null} />
              )}

              {/* Companion plants */}
              {species.companionPlants && (
                <TipSection icon={Users} title="Растения-компаньоны" content={species.companionPlants} />
              )}

              {/* Outdoor suitability */}
              {species.outdoorSuitability && (
                <TipSection icon={TreePine} title="Пригодность для улицы" highlight={outdoorSuitabilityText[species.outdoorSuitability]} content={null} />
              )}

              {/* Common name synonyms */}
              {species.commonNameSynonyms && (
                <div className="flex gap-3 tg-card">
                  <div className="w-10 h-10 rounded-full glass-circle flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">💬</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm">Также известно как</h4>
                    <p className="text-tg-hint text-sm mt-1">{species.commonNameSynonyms}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
